import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { getLoginUrl, getAppleLoginUrl, isAppleSignInConfigured } from "@/const";
import { useLanguageRouter } from "@/hooks/useLanguageRouter";
import { Loader2, ArrowLeft } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "sonner";
import posthog from "posthog-js";
import { getAttributionPayload, getStoredGclid } from "@/lib/attribution";
import {
  trackAuthModalOpened,
  trackAuthOptionClicked,
  trackLogin,
  type AuthModalTrigger,
} from "@/lib/dataLayer";
import { signInStepFromReply, type SignInStep } from "@/lib/signInStep";

interface AuthModalProps {
  open: boolean;
  onClose: () => void;
  redirectPath?: string;
  /** What opened the window, for `auth_modal_opened` (see dataLayer.ts). */
  trigger?: AuthModalTrigger;
  /** Replaces "Sign up or login to your account", e.g. to say a track is saved. */
  subtitle?: string;
}

export default function AuthModal({ open, onClose, redirectPath, trigger = "unknown", subtitle }: AuthModalProps) {
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState<"email" | SignInStep>("email");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const { t } = useTranslation();
  const { withLanguagePath } = useLanguageRouter();
  const appleEnabled = isAppleSignInConfigured();
  const legalLinkClassName =
    "text-[#cdd4de] no-underline border-b border-[rgba(205,212,222,0.4)] pb-px transition-colors hover:text-white hover:border-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0b0f16]";

  const handleVerifyCodeMutation = async ({
    email,
    code,
    invalidMessage,
  }: {
    email: string;
    code: string;
    /** Replaces the server's wrong-code message (a 400), e.g. to say "password". */
    invalidMessage?: string;
  }) => {
    setIsVerifying(true);
    try {
      const attribution = getAttributionPayload();
      const response = await fetch('/api/auth/verify-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(attribution.firstTouchChannel ? { 'x-solmi-first-touch-channel': attribution.firstTouchChannel } : {}),
          ...(attribution.firstTouchSource ? { 'x-solmi-first-touch-source': attribution.firstTouchSource } : {}),
          ...(attribution.lastTouchChannel ? { 'x-solmi-last-touch-channel': attribution.lastTouchChannel } : {}),
          ...(attribution.lastTouchSource ? { 'x-solmi-last-touch-source': attribution.lastTouchSource } : {}),
        },
        credentials: 'include', // Important: include cookies
        // Attribution also rides in the body: proxies can strip the custom
        // x-solmi-* headers (attribution landed NULL on email signups), but a
        // JSON body always arrives — same reason gclid is in the body.
        body: JSON.stringify({ email, code, gclid: getStoredGclid(), attribution }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Verification failed' }));
        throw new Error(
          (response.status === 400 && invalidMessage) ||
            error.error ||
            'Invalid or expired verification code',
        );
      }

      const result = await response.json();
      console.log("[AuthModal] Verification successful, result:", result);
      toast.success(t("auth.loginSuccess", { defaultValue: "Login successful!" }));

      // For new users: useAuth.ts fires the sign_up event after the page
      // reload (via createdAt-based detection) — this is more reliable than
      // firing here because PostHog may not flush the event before the
      // window.location navigation below.
      if (result.isNewUser) {
        try { localStorage.setItem('solmi_tour_pending', '1'); } catch {}
      } else {
        posthog.capture('login', { method: 'email' });
        trackLogin("email");
      }

      // Close modal first
      onClose();

      // Reload page to refresh auth state (no need to call refetchAuth since page reload will handle it)
      console.log("[AuthModal] Reloading page to refresh auth state");
      if (redirectPath) {
        window.location.href = redirectPath;
      } else {
        window.location.reload();
      }
      return result;
    } catch (error: any) {
      toast.error(error.message || t("auth.verifyCodeError", { defaultValue: "Invalid verification code" }));
      throw error;
    } finally {
      setIsVerifying(false);
    }
  };

  // One event per opening, not per render; a trigger relabelled while the
  // window is already open doesn't count as a second opening.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) trackAuthModalOpened(trigger);
    wasOpenRef.current = open;
  }, [open, trigger]);

  const handleGoogleLogin = () => {
    trackAuthOptionClicked("google", trigger);
    window.location.href = getLoginUrl(redirectPath);
  };

  const handleAppleLogin = () => {
    trackAuthOptionClicked("apple", trigger);
    try {
      window.location.href = getAppleLoginUrl(redirectPath);
    } catch (error) {
      console.error("[AuthModal] Apple sign-in unavailable:", error);
      toast.error(
        t("auth.appleUnavailable", {
          defaultValue: "Apple sign-in is unavailable right now. Please try another method.",
        }),
      );
    }
  };

  const handleSendCode = async () => {
    if (!email || !email.includes("@")) {
      toast.error(t("auth.invalidEmail", { defaultValue: "Please enter a valid email address" }));
      return;
    }
    setIsSendingCode(true);
    try {
      const response = await fetch('/api/trpc/auth.sendVerificationCode', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ json: { email } }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: { message: 'Failed to send code' } }));
        throw new Error(error?.error?.message || error?.[0]?.error?.message || 'Failed to send verification code');
      }
      // The store review accounts sign in with a password and are emailed
      // nothing, so there's no code to announce.
      const nextStep = signInStepFromReply(await response.json().catch(() => null));
      if (nextStep === "code") {
        toast.success(t("auth.codeSent", { defaultValue: "Verification code sent! Check your email." }));
      }
      // "Resend code" runs this again from the code step; count the choice once.
      if (step === "email") trackAuthOptionClicked("email", trigger);
      setStep(nextStep);
    } catch (error: any) {
      toast.error(error.message || t("auth.sendCodeError", { defaultValue: "Failed to send verification code" }));
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error(t("auth.invalidCode", { defaultValue: "Please enter a 6-digit code" }));
      return;
    }
    try {
      await handleVerifyCodeMutation({ email, code: verificationCode });
    } catch (error) {
      // Error is already handled in the mutation function
    }
  };

  // The password is the review code, checked by the same verify-email call.
  const handleSignInWithPassword = async () => {
    if (!password) {
      toast.error(t("auth.enterPassword", { defaultValue: "Please enter your password" }));
      return;
    }
    try {
      await handleVerifyCodeMutation({
        email,
        code: password,
        invalidMessage: t("auth.wrongPassword", {
          defaultValue: "Wrong password. Check it and try again.",
        }),
      });
    } catch (error) {
      // Error is already handled in the mutation function
    }
  };

  const handleBackToEmail = () => {
    setStep("email");
    setVerificationCode("");
    setPassword("");
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        showCloseButton={false}
        className="relative block w-full sm:max-w-[600px] overflow-hidden rounded-[30px] border border-white/[0.07] bg-[#0b0f16] px-5 pb-8 pt-12 sm:px-14 sm:pb-11 sm:pt-14 max-h-[92vh] overflow-y-auto shadow-[0_40px_90px_-30px_rgba(0,0,0,0.85)]"
      >
        <DialogTitle className="sr-only">
          {t("auth.welcomeToSolmi", { defaultValue: "Welcome to Solmi" })}
        </DialogTitle>

        {/* ambient accent glow */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 top-[-100px] h-[210px] w-[340px] -translate-x-1/2"
          style={{
            background:
              "radial-gradient(closest-side, rgba(16,185,129,0.16), transparent)",
          }}
        />

        {/* close */}
        <button
          type="button"
          onClick={onClose}
          aria-label={t("common.close")}
          className="absolute right-5 top-5 z-[3] flex h-9 w-9 items-center justify-center rounded-full border border-white/[0.12] bg-white/[0.04] text-[#99a2b0] transition-colors hover:border-white/[0.28] hover:bg-white/[0.08] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60"
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
          <span className="sr-only">{t("common.close")}</span>
        </button>

        {/* brand */}
        <div className="relative flex items-center justify-center">
          <img
            src="/favicon-192x192.png?v=20260920"
            alt="Solmi"
            width={64}
            height={64}
            className="solmi-float h-16 w-16 rounded-[18px] shadow-[0_10px_28px_-8px_rgba(16,185,129,0.6)]"
          />
        </div>

        {/* heading */}
        <div className="relative mt-[22px] text-center">
          <h1 className="m-0 text-[28px] sm:text-[34px] font-extrabold leading-[1.1] tracking-[-0.03em] text-white">
            {t("auth.welcomeToSolmi", { defaultValue: "Welcome to Solmi" })}
          </h1>
          <p className="mt-3 text-base text-[#808a99]">
            {subtitle ?? t("auth.signUpOrLogin", { defaultValue: "Sign up or login to your account" })}
          </p>
        </div>

        {/* oauth buttons */}
        <div className="relative mt-[30px] flex flex-col gap-3">
          <button
            type="button"
            onClick={handleGoogleLogin}
            className="flex h-14 w-full items-center justify-center gap-3 rounded-[15px] border border-white/[0.14] bg-white/[0.02] text-[15.5px] font-semibold text-white transition-all hover:border-white/[0.24] hover:bg-white/[0.05] active:translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06L5.84 9.9C6.71 7.31 9.14 5.38 12 5.38z" />
            </svg>
            {t("auth.continueWithGoogle", { defaultValue: "Continue with Google" })}
          </button>

          {appleEnabled && (
            <button
              type="button"
              onClick={handleAppleLogin}
              className="flex h-14 w-full items-center justify-center gap-3 rounded-[15px] border border-white/[0.14] bg-white/[0.02] text-[15.5px] font-semibold text-white transition-all hover:border-white/[0.24] hover:bg-white/[0.05] active:translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60"
            >
              <svg width="19" height="19" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
                <path d="M17.05 12.94c-.03-2.63 2.15-3.9 2.25-3.96-1.23-1.79-3.14-2.04-3.82-2.07-1.62-.16-3.17.96-3.99.96-.83 0-2.1-.94-3.45-.91-1.77.03-3.41 1.03-4.32 2.62-1.85 3.2-.47 7.94 1.32 10.53.88 1.27 1.92 2.69 3.28 2.64 1.32-.05 1.82-.85 3.41-.85 1.59 0 2.04.85 3.44.82 1.42-.02 2.32-1.29 3.19-2.57 1.01-1.47 1.42-2.9 1.44-2.97-.03-.01-2.76-1.06-2.48-4.81zM14.5 5.18c.73-.89 1.22-2.12 1.08-3.35-1.05.04-2.32.7-3.07 1.58-.67.78-1.26 2.03-1.1 3.23 1.17.09 2.36-.6 3.09-1.46z" />
              </svg>
              {t("auth.continueWithApple", { defaultValue: "Continue with Apple" })}
            </button>
          )}
        </div>

        {/* divider */}
        <div className="my-6 flex items-center gap-[14px]">
          <div className="h-px flex-1 bg-white/10" />
          <span className="text-xs font-semibold tracking-[0.1em] text-[#5c6573]">
            {t("auth.orDivider", { defaultValue: "OR" })}
          </span>
          <div className="h-px flex-1 bg-white/10" />
        </div>

        {step === "email" ? (
          /* email */
          <div className="relative flex items-center">
            <label htmlFor="auth-email" className="sr-only">
              {t("auth.enterEmail")}
            </label>
            <input
              id="auth-email"
              type="email"
              name="email"
              autoComplete="email"
              placeholder={t("auth.continueWithEmail", { defaultValue: "Continue with your email" })}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSendCode();
              }}
              disabled={isSendingCode}
              spellCheck={false}
              className="h-14 w-full rounded-[15px] border border-white/[0.14] bg-white/[0.02] pl-5 pr-[60px] text-[15.5px] text-white outline-none transition-all placeholder:text-[#6a7482] focus:border-[rgba(16,185,129,0.7)] focus:shadow-[0_0_0_4px_rgba(16,185,129,0.1)] disabled:opacity-60"
            />
            <button
              type="button"
              onClick={handleSendCode}
              disabled={isSendingCode || !email}
              aria-label={t("auth.continueWithEmail", { defaultValue: "Continue with your email" })}
              className="absolute right-3 flex h-[38px] w-[38px] items-center justify-center rounded-[10px] bg-transparent text-[#34e0a1] transition-all hover:translate-x-0.5 hover:bg-[rgba(16,185,129,0.12)] disabled:opacity-50 disabled:hover:translate-x-0 disabled:hover:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60"
            >
              {isSendingCode ? (
                <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              )}
            </button>
          </div>
        ) : step === "password" ? (
          /* password: the store review accounts */
          <div className="flex flex-col gap-4">
            <button
              type="button"
              onClick={handleBackToEmail}
              className="flex items-center justify-center gap-2 text-sm text-[#808a99] transition-colors hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              {t("auth.backToEmail", { defaultValue: "Back to email" })}
            </button>

            <p className="text-center text-sm text-[#808a99]">
              {t("auth.enterPasswordFor", { defaultValue: "Enter the password for" })}{" "}
              <span className="font-medium text-white">{email}</span>
            </p>

            <div>
              <label htmlFor="auth-password" className="sr-only">
                {t("auth.password", { defaultValue: "Password" })}
              </label>
              <input
                id="auth-password"
                type="password"
                name="password"
                autoComplete="current-password"
                placeholder={t("auth.password", { defaultValue: "Password" })}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSignInWithPassword();
                }}
                disabled={isVerifying}
                spellCheck={false}
                className="h-14 w-full rounded-[15px] border border-white/[0.14] bg-white/[0.02] px-5 text-[15.5px] text-white outline-none transition-all placeholder:text-[#6a7482] focus:border-[rgba(16,185,129,0.7)] focus:shadow-[0_0_0_4px_rgba(16,185,129,0.1)] disabled:opacity-60"
              />
            </div>

            <button
              type="button"
              onClick={handleSignInWithPassword}
              disabled={isVerifying || !password}
              className="flex h-14 w-full items-center justify-center rounded-[15px] text-[15.5px] font-semibold text-[#04120c] shadow-[0_16px_40px_-12px_rgba(16,185,129,0.5)] transition-all hover:-translate-y-px hover:brightness-105 active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300"
              style={{ background: "linear-gradient(150deg, #34e0a1 0%, #059669 100%)" }}
            >
              {isVerifying ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                  <span>{t("auth.signingIn", { defaultValue: "Signing in…" })}</span>
                </span>
              ) : (
                <span>{t("auth.signIn", { defaultValue: "Sign in" })}</span>
              )}
            </button>
          </div>
        ) : (
          /* code */
          <div className="flex flex-col gap-4">
            <button
              type="button"
              onClick={handleBackToEmail}
              className="flex items-center justify-center gap-2 text-sm text-[#808a99] transition-colors hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              {t("auth.backToEmail", { defaultValue: "Back to email" })}
            </button>

            <p className="text-center text-sm text-[#808a99]">
              {t("auth.codeSentTo", { defaultValue: "We sent a verification code to" })}{" "}
              <span className="font-medium text-white">{email}</span>
            </p>

            <div>
              <label htmlFor="auth-code" className="sr-only">
                {t("auth.enterCode", { defaultValue: "Enter 6-digit code" })}
              </label>
              <input
                id="auth-code"
                type="text"
                inputMode="numeric"
                name="verification-code"
                autoComplete="one-time-code"
                placeholder={t("auth.enterCode", { defaultValue: "Enter 6-digit code" })}
                value={verificationCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                  setVerificationCode(value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleVerifyCode();
                }}
                disabled={isVerifying}
                maxLength={6}
                spellCheck={false}
                className="h-14 w-full rounded-[15px] border border-white/[0.14] bg-white/[0.02] px-5 text-center font-mono text-[18px] tracking-[0.4em] text-white outline-none transition-all placeholder:tracking-normal placeholder:text-[#6a7482] focus:border-[rgba(16,185,129,0.7)] focus:shadow-[0_0_0_4px_rgba(16,185,129,0.1)] disabled:opacity-60"
              />
            </div>

            <button
              type="button"
              onClick={handleVerifyCode}
              disabled={isVerifying || verificationCode.length !== 6}
              className="flex h-14 w-full items-center justify-center rounded-[15px] text-[15.5px] font-semibold text-[#04120c] shadow-[0_16px_40px_-12px_rgba(16,185,129,0.5)] transition-all hover:-translate-y-px hover:brightness-105 active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300"
              style={{ background: "linear-gradient(150deg, #34e0a1 0%, #059669 100%)" }}
            >
              {isVerifying ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                  <span>{t("auth.verifying", { defaultValue: "Verifying…" })}</span>
                </span>
              ) : (
                <span>{t("auth.verifyCode", { defaultValue: "Verify Code" })}</span>
              )}
            </button>

            <button
              type="button"
              onClick={handleSendCode}
              disabled={isSendingCode}
              className="text-sm text-[#808a99] transition-colors hover:text-[#34e0a1] disabled:opacity-50"
            >
              {t("auth.resendCode", { defaultValue: "Resend code" })}
            </button>
          </div>
        )}

        {/* terms */}
        <p className="relative mt-6 text-center text-[12.5px] leading-relaxed text-[#626b79]">
          <Trans
            i18nKey="auth.legalAgreement"
            defaults="By signing in, you agree to our <terms>Terms of Service</terms> and <privacy>Privacy Policy</privacy>."
            components={{
              terms: (
                <a
                  href={withLanguagePath("/terms")}
                  target="_blank"
                  rel="noreferrer"
                  className={legalLinkClassName}
                />
              ),
              privacy: (
                <a
                  href={withLanguagePath("/privacy")}
                  target="_blank"
                  rel="noreferrer"
                  className={legalLinkClassName}
                />
              ),
            }}
          />
        </p>
      </DialogContent>
    </Dialog>
  );
}
